import { Navigate, BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import LockScreen from "@/pages/LockScreen";
import Dashboard from "@/pages/Dashboard";
import PastDay from "@/pages/PastDay";
import Settings from "@/pages/Settings";
import SeasonConfigPage from "@/pages/SeasonConfig";
import WebhooksPage from "@/pages/Webhooks";
import Archive from "@/pages/Archive";
import Reports from "@/pages/Reports";
import CalendarPage from "@/pages/Calendar";
import StaffAbsenceForm from "@/pages/StaffAbsenceForm";
import StaffCards from "@/pages/StaffCards";
import VisitorSignIn from "@/pages/VisitorSignIn";
import AdminVisitors from "@/pages/AdminVisitors";
import StaffCheckIn from "@/pages/StaffCheckIn";
import AdminStaffCheckIn from "@/pages/AdminStaffCheckIn";
import Orientation from "@/pages/Orientation";
import OrientationGames from "@/pages/OrientationGames";
import PlannedAbsencesView from "@/pages/PlannedAbsencesView";
import PlannedLatenessesView from "@/pages/PlannedLatenessesView";
import Help from "@/pages/Help";
import { getToken } from "@/lib/api";

function Protected({ children }) {
  // SESSION HANDOFF (per-tab sessionStorage workaround):
  // If we landed here via `window.open(..., "_blank")`, the new tab has a
  // FRESH (empty) sessionStorage and `getToken()` returns null — sending us
  // to /. To preserve the user's auth across "open in new tab" actions for
  // viewer pages, the opener can pass the current token+level in the URL
  // hash (`#t=...&l=...`). We hydrate sessionStorage and strip the hash
  // before rendering. Hash never goes over the wire to the server.
  if (!getToken() && typeof window !== "undefined" && window.location.hash.startsWith("#t=")) {
    try {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const t = params.get("t");
      const l = params.get("l") || "admin";
      if (t) {
        // Reuse the same shape `setSession` uses (avoid an import cycle).
        sessionStorage.setItem("rrdc_token", t);
        sessionStorage.setItem("rrdc_level", l);
        sessionStorage.setItem("rrdc_token_ts", String(Date.now()));
        // Strip the hash so a stray copy-paste of the URL doesn't leak the token.
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    } catch (_) { /* fall through to redirect */ }
  }
  if (!getToken()) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        <Route path="/" element={<LockScreen />} />
        <Route path="/staffabsenceform" element={<StaffAbsenceForm />} />
        <Route path="/crew-sign-in" element={<Navigate to="/staffabsenceform" replace />} />
        <Route path="/planned-absences" element={<Navigate to="/staffabsenceform" replace />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/past-day" element={<Protected><PastDay /></Protected>} />
        <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
        <Route path="/archive" element={<Protected><Archive /></Protected>} />
        <Route path="/reports" element={<Protected><Reports /></Protected>} />
        <Route path="/staff-cards" element={<Protected><StaffCards /></Protected>} />
        <Route path="/visitor" element={<VisitorSignIn />} />
        <Route path="/staff-checkin" element={<StaffCheckIn />} />
        <Route path="/admin/visitors" element={<Protected><AdminVisitors /></Protected>} />
        <Route path="/admin/staff-checkin" element={<Protected><AdminStaffCheckIn /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/settings/seasons" element={<Protected><SeasonConfigPage /></Protected>} />
        <Route path="/settings/webhooks" element={<Protected><WebhooksPage /></Protected>} />
        <Route path="/orientation" element={<Protected><Orientation /></Protected>} />
        <Route path="/orientation/games" element={<Protected><OrientationGames /></Protected>} />
        <Route path="/reports/planned-absences" element={<Protected><PlannedAbsencesView /></Protected>} />
        <Route path="/reports/planned-latenesses" element={<Protected><PlannedLatenessesView /></Protected>} />
        <Route path="/help" element={<Protected><Help /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
